import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { NavLink } from '@sorare/routing';

import { LineupSet } from '@sorare/core/src/atoms/icons/LineupSet';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineS, LabelM } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_MY_CIRCLES_EVENTTYPE_FIXTURE_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { UserNetworkInfo } from 'components/network/UserNetwork/UserNetworkInfo';
import { NeonRewardConfigs } from 'components/rewards/NeonRewards/NeonRewardConfigs';

import type {
  UserFixtureResultLink_so5Fixture,
  UserFixtureResultLink_userFixtureResults,
} from './__generated__/index.graphql';

const UserNetworkInfoWrapper = styled.div`
  position: relative;
  isolation: isolate;
  border-radius: var(--radius-l);
  overflow: hidden;
  border: 1px solid var(--c-nd-150);
  background-color: var(--c-nd-50);
  &:hover {
    border-color: var(--c-nd-300);
  }
  .active & {
    border-color: var(--c-blue-400);
  }
`;
const LineupSetWrapper = styled(Horizontal)`
  position: absolute;
  z-index: 1;
  left: var(--double-unit);
  top: var(--double-unit);
`;

type Props = {
  userFixtureResults: UserFixtureResultLink_userFixtureResults;
  so5Fixture: UserFixtureResultLink_so5Fixture;
};

export const UserFixtureResultLink = ({
  userFixtureResults,
  so5Fixture,
}: Props) => {
  const { generateSportContextPath } = useSportContext();

  return (
    <NavLink
      key={userFixtureResults.user.slug}
      replace
      to={generateSportContextPath(
        ANY_SPORT_MY_CIRCLES_EVENTTYPE_FIXTURE_SLUG,
        {
          params: {
            slug: userFixtureResults.user.slug,
            fixture: so5Fixture.slug,
            eventType: so5Fixture.type,
          },
        }
      )}
    >
      <UserNetworkInfoWrapper>
        <LineupSetWrapper gap={0.5}>
          <LineupSet />
          <LabelM bold>
            {userFixtureResults.so5LeaderboardContenders?.totalCount}
          </LabelM>
        </LineupSetWrapper>
        <UserNetworkInfo
          user={userFixtureResults.user}
          disableRedirection
          UserNameWrapper={HeadlineS}
        >
          <NeonRewardConfigs
            rewardConfigs={userFixtureResults.totalRewardConfigs}
            size={16}
            iconSize={2}
            displayLabels={false}
            myCompletedThresholdsStreakTasksCount={
              userFixtureResults.completedThresholdsStreakTasksCount
            }
          />
        </UserNetworkInfo>
      </UserNetworkInfoWrapper>
    </NavLink>
  );
};

UserFixtureResultLink.fragments = {
  userFixtureResults: gql`
    fragment UserFixtureResultLink_userFixtureResults on UserFixtureResults {
      slug
      user {
        slug
        ...UserNetworkInfo_publicUserInfoInterface
      }
      completedThresholdsStreakTasksCount
      totalRewardConfigs {
        id
        ...NeonRewardConfigs_rewardConfig
      }
      so5LeaderboardContenders {
        totalCount
      }
    }
    ${UserNetworkInfo.fragments.user}
    ${NeonRewardConfigs.fragments.rewardConfig}
  ` as TypedDocumentNode<UserFixtureResultLink_userFixtureResults>,
  so5Fixture: gql`
    fragment UserFixtureResultLink_so5Fixture on So5Fixture {
      slug
      type
    }
  ` as TypedDocumentNode<UserFixtureResultLink_so5Fixture>,
};
